import { Accordion, Disclosure, DisclosurePanel, DisclosureTitle } from '@adobe/react-spectrum';
import { useBreakpoint } from '@react-spectrum/utils';
import React from 'react';
import styled from 'styled-components';

import UnstyledList from '~/components/styled-components/UnstyledList';

import { CategoryLinksList, StyledLink } from './CategoryLinksList';
import { FOOTER_CATEGORIES, FOOTER_SECONDARY_ROUTES } from './constants';

const YEAR = new Intl.DateTimeFormat('en-US', { year: 'numeric' }).format(new Date());

const WideFooterNav = () => {
  return (
    <WideFooterList>
      {FOOTER_CATEGORIES.map((category) => (
        <li key={category.analyticsId}>
          <CategoryHeading daa-ll={category.analyticsId}>{category.label}</CategoryHeading>
          <CategoryLinksList links={category.links} />
        </li>
      ))}
    </WideFooterList>
  );
};

const AccordionFooterNav = () => {
  return (
    <Accordion>
      {FOOTER_CATEGORIES.map((category) => (
        <Disclosure key={category.analyticsId} id={category.analyticsId}>
          <DisclosureTitle>{category.label}</DisclosureTitle>
          <DisclosurePanel>
            <CategoryLinksList links={category.links} />
          </DisclosurePanel>
        </Disclosure>
      ))}
    </Accordion>
  );
};

const Footer = () => {
  const { matchedBreakpoints } = useBreakpoint();
  const isSmallerScreen = !matchedBreakpoints.includes('L');

  return (
    <StyledFooter>
      <nav aria-label="footer">{isSmallerScreen ? <AccordionFooterNav /> : <WideFooterNav />}</nav>
      <FooterSecondaryNav>
        <CopyrightText>Copyright © {YEAR} Adobe. All rights reserved.</CopyrightText>
        {FOOTER_SECONDARY_ROUTES.map((route) => (
          <FooterSecondaryLink
            key={route.analyticsId}
            href={route.href}
            daa-ll={route.analyticsId}
            data-nav-action={route.dataNavAction}
            target="_blank"
          >
            {route.image && <img src={route.image} alt="" />}
            {route.label}
          </FooterSecondaryLink>
        ))}
      </FooterSecondaryNav>
    </StyledFooter>
  );
};

const CategoryHeading = styled.h3`
  margin-bottom: 8px;
  margin-top: 0;
  font-size: 18px;
`;

const WideFooterList = styled(UnstyledList)`
  max-width: 1400px;
  display: flex;
  justify-content: space-between;
  margin-inline: auto;
`;

const StyledFooter = styled.footer`
  background-color: #fafafa;
  padding: 40px 20px;
`;

const FooterSecondaryNav = styled.div`
  --gap: 20px;
  max-width: 1400px;
  display: flex;
  margin-top: 32px;
  margin-inline: auto;
  column-gap: var(--gap);
  justify-content: end;
  align-items: center;
  flex-wrap: wrap;
`;

const CopyrightText = styled.span`
  font-size: 12px;
`;

const FooterSecondaryLink = styled(StyledLink)`
  --font-size: 12px;
  font-size: var(--font-size);
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;

  &::after,
  &:nth-child(2)::before {
    content: '';
    width: 1px;
    height: var(--font-size);
    position: absolute;
    display: block;
    right: calc(calc(var(--gap) / 2) * -1);
    top: calc(calc(100% - var(--font-size)) / 2);
    background-color: currentColor;
  }

  &:nth-child(2)::before {
    right: 100%;
    left: calc(calc(var(--gap) / 2) * -1);
  }

  &:last-child::after {
    content: none;
  }

  img {
    width: 25px;
    height: 25px;
    margin-left: -12.5px;
  }
`;

export default Footer;
